/**
 * 🎛️ 10-09 (Nina: the event buttons "should be able to work while the user is on the full
 * scene page (not the chat tab)"): which of the message box's event buttons the full-screen
 * picture of a room shows, and how each one looks there. Pure.
 *
 * The scene shows only what exists because an event is on, in the message box's order:
 *   · standing in an outing: its own verbs (🍎 /pick · the maze's /left /right)
 *   · anywhere else: the room's event-only verbs (/bake /taste at the bake-off · /count on a storm night)
 *   · Carve, first, wherever the carving table is open (the patch and the porch)
 * Everyday verbs (/toast /hide /snap…) stay in the message box; the scene has its own 📸 and Share.
 */
import { ROOM_VERBS, eventOnlyChips, chipTapCommand } from './roomVerbs';
import { outingVerbs } from './calendarView';
import { CARVE_CHIP, withCarveChip, carveChipHere } from './pumpkinCarve';

/** The scene's event buttons for the room you are in ([] = no row). `live` = the room verbs the
 *  backend lists (liveRoomVerbs), `outing` = house.outing, `pumpkins` = house.pumpkins. */
export function sceneCommandChips({ roomId, live, outing = null, pumpkins = null } = {}) {
  if (!roomId || roomId === 'house') return [];
  // an outing with no verbs of its own (the patch, the maze once you are out) falls through to the
  // room's event-only verbs, the way the message box falls through to the room's chips
  const base = ((outing && outing.id === roomId) ? outingVerbs(outing) : null) || eventOnlyChips(roomId, live);
  return withCarveChip(base, carveChipHere(pumpkins, roomId)) || [];
}

/** What the house said to a press on the scene: the submit's reply → { ok, line }. A refusal
 *  comes back as a 200 with a private line (asleep, the wrong room, the maze's "Dead end", a full
 *  basket), not an error, and the chat's notice line that shows it sits under the picture, so the
 *  scene shows the line itself. `line` = the server's own words, never a client-only open's
 *  keyword (`room_list`); `ok` = it went through (no line, or a line riding a posted card). */
export function sceneAnswer(resp) {
  const said = (resp && !resp.room_list && typeof resp.private_message === 'string') ? resp.private_message.trim() : '';
  return { ok: !said || !!(resp && (resp.success === true || resp.action)), line: said || null };
}

const OUTING_FACES = {
  pick: { emoji: '🍎', word: 'Pick' },
  left: { emoji: '←', word: 'Left' },
  right: { emoji: '→', word: 'Right' },
};
const verbOf = (chip) => (typeof chip === 'string' && chip[0] === '/' ? chip.slice(1).trim().toLowerCase() : '');
const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

/** How a chip looks on the picture (the Wish button's look: an emoji, then a word).
 *  → { key, emoji, word, label } (label = what a screen reader and the hover title say). */
export function sceneChipFace(chip) {
  if (chip === CARVE_CHIP) return { key: 'carve', emoji: '🎃', word: 'Carve', label: 'Carve a pumpkin' };
  const name = verbOf(chip);
  if (OUTING_FACES[name]) return { key: name, ...OUTING_FACES[name], label: OUTING_FACES[name].word };
  const v = ROOM_VERBS[name];
  if (v) return { key: name, emoji: v.emoji || '', word: cap(name), label: v.description || cap(name) };
  const word = String(chip || '').trim();
  return { key: word.toLowerCase(), emoji: '', word, label: word };
}

/** Does this chip ask for words first (a one-line box on the picture) instead of sending on a tap? */
export function sceneChipNeedsWords(chip) {
  return chip !== CARVE_CHIP && !chipTapCommand(chip);
}

/** The one-line box's hint for a chip that needs words ('maple apple with a burnt lattice'). */
export function sceneChipHint(chip) {
  const v = ROOM_VERBS[verbOf(chip)];
  return (v && v.argHint) || 'a few words';
}

/** The command name a chip sends ('/bake ' → 'bake'), or '' for Carve and anything unknown. */
export function sceneChipVerb(chip) {
  return chip === CARVE_CHIP ? '' : verbOf(chip);
}
